import React, { useState } from 'react'
// types
import {
  ASSETS_TYPES,
  assetsCollectionElement,
  IEcommerceAnswerSchema,
  IEcommerceVideoAsset,
  IFileAsset
} from '../../types'
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types'
import { getMediumAssetPath } from '@aytm/lib/common/assets'

// custom hooks
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline'

// functions
import { assetDialogEvent, getAssetType } from '../../functions'

// components
import '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Store/ModelViewer.ts'
import AssetsModal from './AssetsModal'
import Carousel from '@aytm/app/swidget/base/questions/Ecommerce/components/Carousel.tsx'

// styles
import style from './AssetsBlock.module.scss'

interface IAssetsBlockProps {
  answer: IEcommerceAnswerSchema
  assetsCollection: assetsCollectionElement[]
}

const AssetsBlock = ({ answer, assetsCollection }: IAssetsBlockProps) => {
  const { t } = useSurveyLocale()
  const viewport = useViewport()
  const timeline = useTimeline()

  const [modalAssetIndex, setModalAssetIndex] = useState<number | null>(null)

  const onAssetClick = (assetIndex: number) => {
    timeline.log({
      ref: answer.id,
      type: assetDialogEvent('open', assetsCollection[assetIndex])
    })
    setModalAssetIndex(assetIndex)
  }

  // The modal logs the typed close itself — it owns the on-screen asset the close is typed by.
  const onModalClose = () => setModalAssetIndex(null)

  const renderAsset = (asset: assetsCollectionElement, index: number) => (
    <div className={style.assetCard} key={index} onClick={() => onAssetClick(index)}>
      {ASSETS_TYPES.IMAGE === getAssetType(asset) && (
        <div
          className={style.imageAsset}
          role="img"
          aria-label={(asset as IImageAsset).alt_text || t('questions', 'img', 'noAlt')}
          style={{ backgroundImage: `url(${getMediumAssetPath(asset as IImageAsset)})` }}
        />
      )}
      {ASSETS_TYPES.VIDEO === getAssetType(asset) && (
        <div
          className={style.videoAsset}
          role="presentation"
          style={{ backgroundImage: `url(${(asset as IEcommerceVideoAsset).video.thumbnail})` }}
        />
      )}
      {ASSETS_TYPES.MODEL === getAssetType(asset) && (
        <div className={style.modelAsset}>
          <model-viewer src={(asset as IFileAsset).file_url} camera-controls={false} interaction-prompt="none" />
          <div className={style.modelLayout} />
        </div>
      )}
      <div className={style.zoom}>
        <div className={style.zoomButton}>
          <i className="pi pi-search" />
        </div>
      </div>
    </div>
  )

  return (
    <div className={`${style.images} ${viewport.mobile ? style.imagesMobile : ''}`} data-testid="SW-EC-AssetsBlock">
      {viewport.mobile ? (
        <Carousel schema={answer} store="costco" pagination="costco" openDialog={onAssetClick} />
      ) : (
        <>
          {renderAsset(assetsCollection[0], 0)}
          {assetsCollection.length > 1 && (
            <div className={style.assetsGrid}>
              {assetsCollection.slice(1).map((asset, index) => renderAsset(asset, index + 1))}
            </div>
          )}
        </>
      )}
      {null !== modalAssetIndex && (
        <AssetsModal
          answer={answer}
          assetsCollection={assetsCollection}
          index={modalAssetIndex}
          onClose={onModalClose}
        />
      )}
    </div>
  )
}

export default AssetsBlock
